import { AnalyticsService } from 'base/analytics';
import { LibraryCache } from 'base/library_cache';
import { ToastQueue } from 'base/ui/toast';
import { computedFn } from 'mobx-utils';
import { Assistant } from '../ai/assistant';
import {
  aPage,
  aSpace,
  ElementInstance,
  Page,
  SectionRef,
  SectionState,
  Space,
} from '../model';
import { SelectionState } from '../selection/states';
import { SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { SidebarNavigation } from '../sidebar/routes';
import { EditingActionConfirmations } from './confirmations';
import { ElementNodeEditingActions } from './element_node';
import { addGlobalSection } from './global_sections';
import {
  dropPageAt,
  insertPage,
  onAfterDropPage,
  PageEditingActions,
} from './page';
import {
  dropSectionAt,
  onAfterDropSection,
  SectionEditingActions,
} from './section';
import {
  dropSpaceAt,
  HomepageSpaceEditingActions,
  SpaceEditingActions,
} from './space';

export class ProjectEditingActions {
  constructor(
    readonly selection: SelectionStore,
    readonly session: ProjectSession,
    readonly toastQueue: ToastQueue,
    readonly sidebarNavigation: SidebarNavigation,
    readonly assistant: Assistant,
    private readonly analytics: AnalyticsService,
    readonly library: LibraryCache,
  ) {}

  readonly confirmations = new EditingActionConfirmations();

  readonly addSpaceToEnd = () => {
    const { spaces } = this.selection.project;
    this.addSpaceAfter(spaces[spaces.length - 1] ?? 'home');
  };

  readonly addSpaceAfter = (space: Space | 'home') => {
    const { spaces } = this.selection.project;
    const index = space === 'home' ? -1 : spaces.indexOf(space);
    spaces.splice(
      index + 1,
      0,
      aSpace({
        name: `Sitemap ${spaces.length + 1}`,
        subPages: [aPage({ name: 'Home' })],
      }),
    );
    this.selection.set({ space: spaces[index + 1] });
    this.sidebarNavigation.goto({ path: 'edit' });
  };

  readonly homepageSpace = new HomepageSpaceEditingActions(
    this.selection,
    this.confirmations,
  );
  readonly forSpace = computedFn(
    (space: Space | 'home') =>
      space === 'home'
        ? this.homepageSpace
        : new SpaceEditingActions(this.selection, space, this.confirmations),
    { name: 'EditingActions.forSpace' },
  );
  readonly dropSpaceAt = dropSpaceAt(this.selection);

  readonly forPage = computedFn(
    (page: Page) => {
      const { space } = this.selection.factory.forPage(page);
      return new PageEditingActions(
        this.selection,
        this.session,
        this.assistant,
        this.sidebarNavigation,
        space,
        page,
      );
    },
    { name: 'EditingActions.forPage' },
  );
  readonly dropPageAt = dropPageAt(this.selection);
  readonly onAfterDropPage = onAfterDropPage(this.selection.project);
  addSubPage(parent: Space | Page, index: number) {
    insertPage(parent, index, this.selection);
  }

  /** Adds a page as close to the current selection as possible. */
  addPageNatural() {
    const sel = this.selection.get();
    if (sel.page) {
      this.forPage(sel.page).addPageNatural();
    } else if (sel.space && sel.space !== 'home') {
      this.addSubPage(sel.space, sel.space.subPages.length);
    } else {
      this.addSubPage(this.selection.project.homePage, 0);
    }
  }

  readonly forSection = computedFn(
    (section: SectionRef) => {
      const { page, space } = this.selection.factory.forSection(section);
      return new SectionEditingActions(
        this.session,
        this.selection,
        this.assistant,
        this.sidebarNavigation,
        this.session.undo,
        this.library,
        this.confirmations,
        this.toastQueue,
        this.assistant.textRewriting,
        space,
        page,
        section,
      );
    },
    { name: 'EditingActions.forSection' },
  );
  readonly dropSectionAt = dropSectionAt(this.selection);
  readonly onAfterDropSection = onAfterDropSection;

  forElementNode(
    sel: SelectionState & { element: ElementInstance },
  ): ElementNodeEditingActions {
    return this._forElementNode(
      sel.nodeId,
      sel.element,
      sel.section,
      sel.page,
      sel.space,
      sel.addDirection,
    );
  }

  // Wrapper above is necessary because computedFn memoises args by reference,
  // and while element, section, etc. are going to be referentially stable, the
  // selection object containing them is not.
  private readonly _forElementNode = computedFn(
    (
      nodeId: string | undefined,
      element: ElementInstance,
      section: SectionRef,
      page: Page,
      space: Space | 'home',
      addDirection: 'before' | 'after' | undefined,
    ): ElementNodeEditingActions => {
      return new ElementNodeEditingActions(
        this.selection,
        {
          nodeId,
          element,
          section,
          page,
          space,
          addDirection,
        },
        this.library,
        this.sidebarNavigation,
        this.analytics,
        this.session.flags,
        (sel) => this.forElementNode(sel),
      );
    },
    { name: 'EditingActions.forElementNode' },
  );

  readonly shuffleSectionComponent = (sref: SectionRef) => {
    const { page } = this.selection.factory.forSection(sref);
    if (!this.session.canViewWireframe(page)) {
      return this.session.showUpgradeWall?.('pages');
    }
    this.assistant.shuffleComponent(page, sref);
  };

  /** Merges globals from a clipboard payload into the current project. */
  readonly mergeGlobalSections = (newGlobals: Record<string, SectionState>) => {
    const { project } = this.selection;
    for (const [id, section] of Object.entries(newGlobals)) {
      if (project.globalSections.has(id)) continue;
      addGlobalSection(id, section, project, this.session.undo);
    }
  };
}
